// 🔧 PHASE 1 FIX: Session Context Manager
import { TrainingMode } from '@/utils/trainingGenerator';

export interface SessionContext {
  dayNumber: number;
  roundNumber: number;
  trainingMode: TrainingMode;
  startTime: number;
  fingerConfigHash: string; // For validation
}

/**
 * 🔧 CRITICAL FIX: Stable context preservation
 * Prevents originalSessionContext from becoming undefined during state transitions
 */
export class SessionContextManager {
  private static instance: SessionContextManager;
  private currentContext: SessionContext | null = null;
  private contextHistory: SessionContext[] = [];
  private readonly MAX_HISTORY = 10;

  static getInstance(): SessionContextManager {
    if (!SessionContextManager.instance) {
      SessionContextManager.instance = new SessionContextManager();
    }
    return SessionContextManager.instance;
  }

  /**
   * Start new session with context preservation
   */
  startSession(
    dayNumber: number, 
    roundNumber: number, 
    trainingMode: TrainingMode, 
    fingerConfigHash: string
  ): SessionContext {
    const context: SessionContext = {
      dayNumber,
      roundNumber,
      trainingMode,
      startTime: Date.now(),
      fingerConfigHash
    };

    // Store current context
    this.currentContext = context;
    
    // Add to history
    this.contextHistory.push(context);
    if (this.contextHistory.length > this.MAX_HISTORY) {
      this.contextHistory.shift();
    }

    console.log(`📋 Session context preserved: Day ${dayNumber}, Round ${roundNumber}`);
    return context;
  }

  /**
   * Get current session context (guaranteed to return valid context)
   */
  getCurrentContext(): SessionContext | null {
    return this.currentContext;
  }

  /**
   * Complete session and return validated context
   */
  completeSession(): SessionContext | null {
    const context = this.currentContext;
    
    if (!context) {
      console.warn('⚠️ No context available for session completion');
      // Try to recover from history
      return this.contextHistory[this.contextHistory.length - 1] || null;
    }

    console.log(`✅ Session completed with context: Day ${context.dayNumber}, Round ${context.roundNumber}`);
    return context;
  }

  /**
   * Clear current context
   */
  clearContext(): void {
    this.currentContext = null;
  }

  /**
   * Validate context integrity
   */
  validateContext(context: SessionContext): boolean {
    return !!(
      context.dayNumber >= 1 && 
      context.roundNumber >= 1 && 
      context.trainingMode &&
      context.startTime > 0
    );
  }

  /**
   * Get context history for debugging
   */
  getContextHistory(): SessionContext[] {
    return [...this.contextHistory];
  }
}

/**
 * 🔧 PERFORMANCE FIX: Hash finger configuration for context validation
 */
export const createFingerConfigHash = (fingerConfig: any): string => {
  return JSON.stringify({
    left: fingerConfig.activeFingersLeft?.sort(),
    right: fingerConfig.activeFingersRight?.sort(),
    complexity: fingerConfig.letterComplexity
  });
};

/**
 * 🔧 CRITICAL FIX: Completion state manager to prevent race conditions
 */
export class CompletionStateManager {
  private static instance: CompletionStateManager;
  private completionInProgress = false;
  private completionQueue: Array<() => void> = [];
  private lastCompletionTime = 0;
  private readonly MIN_COMPLETION_INTERVAL = 1000; // 1 second between completions

  static getInstance(): CompletionStateManager {
    if (!CompletionStateManager.instance) {
      CompletionStateManager.instance = new CompletionStateManager();
    }
    return CompletionStateManager.instance;
  }

  /**
   * Execute completion with race condition protection
   */
  executeCompletion(completionFn: () => void): boolean {
    const now = Date.now();
    
    // 🔧 CRITICAL: Prevent rapid-fire completions
    if (this.completionInProgress) {
      console.log('⚠️ Completion already in progress, queuing request');
      this.completionQueue.push(completionFn);
      return false;
    }

    // 🔧 CRITICAL: Minimum interval between completions
    if (now - this.lastCompletionTime < this.MIN_COMPLETION_INTERVAL) {
      console.log('⚠️ Completion too soon after previous, ignoring');
      return false;
    }

    // Execute completion
    this.completionInProgress = true;
    this.lastCompletionTime = now;

    try {
      completionFn();
      console.log('✅ Session completion executed successfully');
    } catch (error) {
      console.error('❌ Session completion error:', error);
    } finally {
      // Clean up and process queue
      setTimeout(() => {
        this.completionInProgress = false;
        this.processQueue();
      }, 500);
    }

    return true;
  }

  /**
   * Process queued completions
   */
  private processQueue(): void {
    if (this.completionQueue.length > 0 && !this.completionInProgress) {
      const nextCompletion = this.completionQueue.shift();
      if (nextCompletion) {
        this.executeCompletion(nextCompletion);
      }
    }
  }

  /**
   * Clear completion state (for cleanup)
   */
  reset(): void {
    this.completionInProgress = false;
    this.completionQueue = [];
    this.lastCompletionTime = 0;
  }

  /**
   * Get completion status
   */
  isInProgress(): boolean {
    return this.completionInProgress;
  }
}
